/**
 * The audit log: the compliance record of a conversation. It is the one screen
 * that keeps timings, because in an audit record they are part of the evidence.
 */
import { useCallback, useMemo, useState } from "react";
import { ProductTree } from "@financial-advisory/data-access/api-types";
import { MessageSquare } from "lucide-react";
import type { ChangedNode, SystemState } from "@unlikelyai-magic/utils";
import type { ConversationPair, InitialMessage } from "../types";
import { AuditLogHeader } from "./AuditLogHeader";
import { AuditLogSummary } from "./AuditLogSummary";
import { ConversationTurn } from "./ConversationTurn";
import { buildAuditTurns } from "./auditTurns";
import { generateAuditLogPDF } from "./generateAuditLogPDF";

export interface AuditLogViewProps {
  /**
   * Initial assistant messages (non-clickable, shown before conversation pairs)
   */
  initialMessages?: InitialMessage[];
  /**
   * List of conversation pairs with all decision-making data
   */
  conversations: ConversationPair[];
  /**
   * Product trees for looking up details
   */
  productTreesById: Map<string, ProductTree>;
  /**
   * Every leaf question across every capability, used to say which outcomes are
   * waiting on the question the platform asked for.
   */
  allNodes: Map<string, ChangedNode>;
  /**
   * System state showing situation/segment/suggestion resolution
   */
  systemState: SystemState;
  /**
   * Session ID for the conversation
   */
  sessionId?: string;
  /**
   * Whether the conversation is complete
   */
  isComplete?: boolean;
  /**
   * Callback to close the view
   */
  onClose: () => void;
}

export const AuditLogView = ({
  initialMessages = [],
  conversations,
  allNodes,
  systemState,
  sessionId,
  isComplete = false,
  onClose,
}: AuditLogViewProps) => {
  const turns = useMemo(() => buildAuditTurns(conversations), [conversations]);

  // Every turn expands, but only the last one starts open, so the record reads as
  // a list until the reader asks for a turn.
  const [expandedTurns, setExpandedTurns] = useState<Set<number>>(
    () => new Set(turns.length > 0 ? [turns[turns.length - 1].number] : [])
  );

  const toggleTurn = (turnNumber: number) =>
    setExpandedTurns((current) => {
      const next = new Set(current);
      if (next.has(turnNumber)) {
        next.delete(turnNumber);
      } else {
        next.add(turnNumber);
      }
      return next;
    });

  const handleDownloadPDF = useCallback(() => {
    generateAuditLogPDF({
      sessionId,
      isComplete,
      conversations,
      initialMessages,
      systemState,
    });
  }, [sessionId, isComplete, conversations, initialMessages, systemState]);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
      <div className="flex h-[90vh] w-[90vw] flex-col overflow-hidden rounded-lg border border-brand-border bg-brand-card shadow-xl">
        <AuditLogHeader
          sessionId={sessionId}
          isComplete={isComplete}
          turnCount={turns.length}
          onClose={onClose}
          onDownloadPDF={handleDownloadPDF}
        />

        <div className="flex min-h-0 flex-1 flex-col gap-3.5 overflow-y-auto px-6 py-5">
          <AuditLogSummary turns={turns} systemState={systemState} />

          {turns.length === 0 ? (
            <div className="rounded-lg border border-brand-border p-12 text-center">
              <MessageSquare
                className="mx-auto mb-3 h-12 w-12 text-brand-muted opacity-50"
                aria-hidden={true}
              />
              <p className="text-sm text-brand-muted">
                No conversation turns recorded yet
              </p>
            </div>
          ) : (
            turns.map((turn, index) => (
              <ConversationTurn
                key={turn.state.id}
                turn={turn}
                previousTurn={index > 0 ? turns[index - 1] : undefined}
                conversations={conversations}
                allNodes={allNodes}
                isExpanded={expandedTurns.has(turn.number)}
                onToggle={() => toggleTurn(turn.number)}
              />
            ))
          )}
        </div>
      </div>
    </div>
  );
};
